import React, { useEffect, useState } from 'react'
import { Grid, TextField, Typography } from '@material-ui/core'
import { Controller, useForm } from 'react-hook-form'
import { isEmpty } from 'lodash'
import { Alert, AlertTitle } from '@material-ui/lab'
import { KeyboardDatePicker } from '@material-ui/pickers'

import { Label } from 'components'
import { FillVariableConfirmationDialog } from './components'

import useDocumentGenerate from 'hooks/useDocumentGenerate'
import schema from './schema'
import helpers from 'helpers'

const FillVariablesStep = () => {
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false)

  const defaultValueByInputType = {
    text: '',
    number: 0,
    date: null,
  }

  const {
    wantsToAdvanceStep,
    setDisabledNextButton,
    documentVariablesFilled,
    setDocumentVariablesFilled,
    resetAdvanceStep,
    handleNext,
    documentVariables,
  } = useDocumentGenerate()

  const variables = documentVariables.map((variable) => ({
    ...variable,
    inputName: `variable-${variable.id}`,
    variableType: variable.variableType || 'text',
  }))

  const mountDefaultValues = (variables) =>
    variables.reduce((acc, variable) => {
      let defaultValue = documentVariablesFilled[variable.inputName]

      if (defaultValue === undefined) {
        defaultValue =
          variable.automaticValue !== undefined
            ? variable.automaticValue
            : defaultValueByInputType[variable.variableType]
      }

      if (variable.variableType === 'date' && defaultValue) {
        defaultValue = helpers.formatters.date(defaultValue, 'YYYY-MM-DD')
      }

      return {
        ...acc,
        [variable.inputName]: defaultValue,
      }
    }, {})

  const { control, errors, formState, handleSubmit, reset } = useForm({
    validationSchema: schema(variables),
    defaultValues: mountDefaultValues(variables),
    mode: 'onChange',
  })

  const onSubmit = (data) => {
    const formattedVariables = {}
    variables.forEach((variable) => {
      if (variable.variableType === 'date') {
        const formattedVariable = helpers.formatters.date(
          data[variable.inputName],
        )

        formattedVariables[variable.inputName] = formattedVariable
      } else {
        formattedVariables[variable.inputName] = data[variable.inputName]
      }
    })

    setDocumentVariablesFilled(formattedVariables)
    setOpenConfirmationDialog(true)
  }

  useEffect(() => {
    setDisabledNextButton(!formState.isValid)
  }, [formState.isValid])

  useEffect(() => {
    if (wantsToAdvanceStep) {
      if (!isEmpty(variables)) {
        handleSubmit(onSubmit)()
      } else {
        handleNext()
      }

      resetAdvanceStep()
    }
  }, [wantsToAdvanceStep])

  useEffect(() => {
    if (!isEmpty(errors) && wantsToAdvanceStep) {
      resetAdvanceStep()
    }
  }, [errors])

  useEffect(() => {
    if (!isEmpty(variables)) reset(mountDefaultValues(variables))
  }, [documentVariables])

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Alert variant="standard" severity="info">
          <AlertTitle>
            Revise e preencha as informações complementares para gerar o
            documento
          </AlertTitle>
          Antes de gerar o documento final, revise cuidadosamente as informações
          já preenchidas e corrija qualquer dado incorreto ou desatualizado. Em
          seguida, complete os campos complementares solicitados, pois algumas
          informações não podem ser preenchidas automaticamente pela plataforma
          e dependem da sua inserção manual.
        </Alert>
      </Grid>
      {!isEmpty(variables) ? (
        <Grid item xs={12}>
          <Grid container spacing={2}>
            {variables?.map((variable) => (
              <Label
                item
                xs={12}
                key={variable.id}
                title={variable.name}
                description={variable.description}
                paddingBoxTitle={0}
              >
                {variable.variableType === 'date' ? (
                  <Controller
                    as={
                      <KeyboardDatePicker
                        fullWidth
                        format="DD/MM/yyyy"
                        error={!!errors[variable.inputName]}
                        helperText={errors[variable.inputName]?.message}
                      />
                    }
                    name={variable.inputName}
                    control={control}
                  />
                ) : (
                  <Controller
                    as={
                      <TextField
                        type={variable.variableType}
                        format="DD/MM/yyyy"
                        color="primary"
                        variant="outlined"
                        error={!!errors[variable.inputName]}
                        helperText={errors[variable.inputName]?.message}
                        fullWidth
                      />
                    }
                    name={variable.inputName}
                    control={control}
                  />
                )}
              </Label>
            ))}
          </Grid>
        </Grid>
      ) : (
        <Grid item xs={12}>
          <Alert severity="info">
            <AlertTitle>Nenhuma variável encontrada</AlertTitle>
            <Typography>
              Este modelo não necessita de mais nenhuma informação para ser
              gerado.
              <br />
              Você pode avançar para a próxima etapa.
            </Typography>
          </Alert>
        </Grid>
      )}
      <FillVariableConfirmationDialog
        open={openConfirmationDialog}
        setOpen={setOpenConfirmationDialog}
        variables={variables}
        variableValues={documentVariablesFilled}
      />
    </Grid>
  )
}

export default FillVariablesStep
